/**
 * AI Prescription search flow – branching logic for new vs follow-up searches.
 * Called from the page after normalizing input and adding the initial conversation.
 */

import React from "react";
import { parseAIPrescriptionText } from "../../Apis/AIPrescriptionApis";
import { ConversationItem } from "../../utils/AIPrescription/types";
import {
  createPayload,
  getSubstitutionModification,
  createPrescriptionAPICallbacks,
  createModificationCallbacks,
  TIMEOUTS,
} from "../../utils/AIPrescription";
import { detectIntents } from "../../utils/AIPrescription/intentDetectionUtils";
import type { AIPrescriptionPayload } from "../../Apis/AIPrescriptionApis";

export interface ExecuteSearchParams {
  normalizedQuery: string;
  query: string;
  conversations: ConversationItem[];
  conversationId: number;
  searchText: string;
  lastConversation: ConversationItem | null;
  preferredPharmacy: string;
  /** Optional WebVTT transcript body from uploaded .vtt */
  vttContent?: string;
  /**
   * Skip the shared debounce and run immediately. The debounce exists to coalesce rapid
   * typed/voice input; explicit button-triggered actions (e.g. Send on a prescription card)
   * must not share that debounce — otherwise sending drug A then quickly sending drug B
   * silently cancels A's pending call before it ever reaches the API (both use the same
   * apiCallTimeoutRef).
   */
  immediate?: boolean;
  resultMessageProcessedRef: React.MutableRefObject<boolean>;
  apiCallTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  isMountedRef: React.MutableRefObject<boolean>;
  setConversations: React.Dispatch<React.SetStateAction<ConversationItem[]>>;
  setIsSearching: (value: boolean) => void;
  handlePrescriptionAPIWrapper: (
    payload: AIPrescriptionPayload,
    conversationId: number,
    options: {
      logContext: string;
      structuredData: any[];
      lastConversation: ConversationItem;
      onSuccess?: (
        response: any,
        updatedStructuredData: any[],
        repromptMessage: string
      ) => { prescriptions: any[]; aiResponse: string; originalPrescription?: any };
      onError?: () => {
        prescriptions: any[];
        aiResponse: string;
        originalPrescription?: any;
      };
    }
  ) => void;
}

/**
 * Runs the delayed search flow: follow-up branches (pharmacy selection, modification) or new prescription search.
 * Confirmation, send, change pharmacy are managed by WSS. Call after the page has added the initial loading conversation.
 */
export function executeSearch(params: ExecuteSearchParams): void {
  const {
    normalizedQuery,
    query,
    conversations,
    conversationId,
    searchText,
    lastConversation,
    preferredPharmacy,
    apiCallTimeoutRef,
    isMountedRef,
    setConversations,
    setIsSearching,
    handlePrescriptionAPIWrapper,
    vttContent,
    immediate,
  } = params;

  const vttOpts = vttContent?.trim() ? { vttContent } : undefined;

  const { isModification } = detectIntents(searchText);

  /** When server is still collecting missing med fields, always send a new parse — do not copy Rx or branch to pharmacy/modification without API. */
  const lastState = String(lastConversation?.currentState ?? "").toLowerCase();
  const isCollectMedication = lastState === "collect_medication";

  const runSearch = () => {
    if (
      lastConversation &&
      !lastConversation.isLoading &&
      !vttOpts &&
      !isCollectMedication
    ) {
      if (
        lastConversation.prescriptions &&
        lastConversation.prescriptions.length > 0
      ) {
        if (preferredPharmacy) {
          const structuredData = lastConversation.structuredMedicationData || [];
          const updatedPrescriptions = lastConversation.prescriptions.map((p: any) => ({
            ...p,
            pharmacy: preferredPharmacy,
          }));
          const pharmacyCallbacks = createPrescriptionAPICallbacks({
            lastConversation,
            preferredPharmacy,
            existingPrescriptions: updatedPrescriptions,
            pharmacy: preferredPharmacy || "",
          });
          handlePrescriptionAPIWrapper(
            createPayload(normalizedQuery, vttOpts),
            conversationId,
            {
              logContext: "pharmacy selection",
              structuredData,
              lastConversation,
              onSuccess: pharmacyCallbacks.onSuccess,
              onError: pharmacyCallbacks.onError,
            }
          );
          return;
        }
      }

      if (
        lastConversation.prescriptions.length > 0 &&
        isModification
      ) {
        const structuredData = lastConversation.structuredMedicationData || [];
        const modifiedSubstitutions = getSubstitutionModification(searchText);
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? { ...conv, pendingSubstitutions: modifiedSubstitutions }
              : conv
          )
        );
        const modificationCallbacks = createModificationCallbacks({
          lastConversation,
          preferredPharmacy,
          modifiedSubstitutions,
        });
        handlePrescriptionAPIWrapper(
          createPayload(query, vttOpts),
          conversationId,
          {
            logContext: "modification",
            structuredData,
            lastConversation,
            onSuccess: modificationCallbacks.onSuccess,
            onError: modificationCallbacks.onError,
          }
        );
        return;
      }
    }

    const payload = createPayload(normalizedQuery, vttOpts);
    parseAIPrescriptionText(payload)
      .then((_response: any) => {
        if (!isMountedRef.current) return;
        return;
      })
      .catch((error: any) => {
        if (!isMountedRef.current) return;
        console.error("API call failed:", error);
      });
  };

  if (immediate) {
    // Explicit user action (e.g. a Send button click) — do not coalesce with the shared
    // debounce timer, and do not cancel any other pending debounced call either.
    runSearch();
    return;
  }

  if (apiCallTimeoutRef.current) {
    clearTimeout(apiCallTimeoutRef.current);
  }
  apiCallTimeoutRef.current = setTimeout(runSearch, TIMEOUTS.API_CALL_DELAY);
}
